import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

// Lightweight first-login welcome. Shown once (preference `onboarded`), only
// after any temporary-password prompt is done. Confirms basic profile, shows a
// role/workspace summary, then lands on the role starter dashboard. No wizard.
export function WelcomeOnboarding() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [perms, setPerms] = useState(null);
  const [landing, setLanding] = useState("/");
  const [name, setName] = useState("");
  const [tz, setTz] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const prefs = await apiFetch("/preferences");
        if (prefs?.onboarded) return;   // already welcomed
        const [me, p] = await Promise.all([apiFetch("/profile/me"), apiFetch("/me/permissions")]);
        let land = "/";
        try { land = (await apiFetch("/me/landing"))?.landing || "/"; } catch { /* ignore */ }
        if (!alive) return;
        setProfile(me); setPerms(p); setLanding(land);
        setName(me.display_name || me.full_name || ""); setTz(me.timezone || "Asia/Kolkata");
        setOpen(true);
      } catch { /* ignore — never block the app */ }
    })();
    return () => { alive = false; };
  }, []);

  const finish = async () => {
    setBusy(true);
    try {
      // best-effort profile edit — must never block the onboarded flag
      try {
        const patch = {};
        if (name && name !== (profile.display_name || profile.full_name)) patch.display_name = name;
        if (tz && tz !== profile.timezone) patch.timezone = tz;
        if (Object.keys(patch).length) await apiFetch("/profile/me", { method: "PUT", body: JSON.stringify(patch) });
      } catch { /* non-fatal — carry on and still mark onboarded */ }
      await apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { onboarded: true } }) });
      setOpen(false);
      toast.success("Welcome to Nirvana ContentOS. Your workspace is ready.");
      if (landing && landing !== window.location.pathname) navigate(landing);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!open || !profile) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" data-testid="welcome-onboarding" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Sparkles className="h-5 w-5 text-accent" /> Welcome to Nirvana ContentOS
          </DialogTitle>
          <DialogDescription>Your workspace is ready. A couple of quick confirmations and you're set.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid="welcome-summary">
            <div className="flex justify-between"><span className="text-muted-foreground">Your role</span><span className="font-medium text-foreground">{perms?.role || "Member"}</span></div>
            <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Starting workspace</span><span className="font-medium text-foreground">{landing === "/" ? "Home dashboard" : landing}</span></div>
          </div>
          <div><Label className="text-xs">Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="welcome-name" /></div>
          <div><Label className="text-xs">Timezone</Label><Input value={tz} onChange={(e) => setTz(e.target.value)} data-testid="welcome-tz" /></div>
          <p className="text-[11px] text-muted-foreground">You can fine-tune everything later in your profile.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={finish} disabled={busy} className="gap-1.5" data-testid="welcome-continue">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Enter my workspace
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
